import { useState } from 'react'
import Formsy from 'formsy-react'

import { userSelector, billingAddressSelector, deliveryAddressSelector } from 'auth/selectors'
import { authVisible } from 'header/actions'
import featureToggles from 'shared/featureToggles'
import { invoicePriceFormatter } from 'shared/utils'
import useRequestPending from 'shared/hooks/useRequestPending'

import { CardSection } from '@components/CardSection'
import ErrorMessage from '@components/ErrorMessage'

import {
    reviewInvoicesSelector,
    subscriptionPurchaseBuyPendingSelector,
    credentialTypeSelector,
} from 'purchase-flow/canada/selectors'
import { CREDENTIAL_DIGITAL, CREDENTIAL_LPR, DELIVERY_COLLECT, DELIVERY_HOME } from 'purchase-flow/constants'

import purchaseStyles from 'purchase-flow/styles.module.scss'

import ProrataSection from './ProrataSection'
import InvoiceSection from './InvoiceSection'
import FooterSection from './FooterSectionNew/FooterSection'
import FooterSectionOld from './FooterSectionOld/FooterSection'

import PaymentMethodSection from './PaymentMethodSection'
import AddressSection from './AddressSection'
import VehiclesSection from './VehiclesSection'

const ReviewPaySelection = ({
    billingAddress,
    userDeliveryAddress,
    reviewInvoices,
    handleBuy,
    user,
    pending,
    onSuccess,
    purchaseData,
    credentialType,
}) => {
    const [paymentMethod, setPaymentMethod] = useState(null)
    const [termsAcceptedDate, setTermsAcceptedDate] = useState(null)
    const { error, isLoading } = useRequestPending(pending, () => onSuccess())
    const { delivery, startDate, vehicles } = purchaseData
    const { prorata } = reviewInvoices

    const totalToPay = invoicePriceFormatter(
        prorata.tax + prorata.amountWithoutTax + (delivery?.rate?.price || delivery?.rate?.amount || 0)
    )

    const disabledSubmit = !paymentMethod || isLoading || (featureToggles.hasSubcriptionLite && vehicles.length === 0)

    const deliveryAddress = purchaseData.deliveryAddress || userDeliveryAddress

    const onSubmit = (data = {}) => {
        handleBuy(
            paymentMethod,
            data.billing || billingAddress,
            data.delivery || deliveryAddress,
            data.termsAcceptedDate || termsAcceptedDate
        )
    }
    const hasLPR = credentialType?.indexOf(CREDENTIAL_LPR) > -1
    const hasDelivery =
        delivery &&
        delivery?.typeCode !== CREDENTIAL_DIGITAL.toUpperCase() &&
        (!featureToggles.hasSubcriptionLite || (delivery?.prdctCd !== DELIVERY_COLLECT && !hasLPR))

    return (
        <CardSection className={purchaseStyles.contentSection}>
            <Formsy onSubmit={onSubmit}>
                {reviewInvoices && (
                    <>
                        <InvoiceSection invoice={reviewInvoices.nextMonth} />
                        <ProrataSection
                            delivery={delivery}
                            prorataInvoice={prorata}
                            total={totalToPay}
                            startDate={startDate}
                        />
                        {featureToggles.hasSubcriptionLite && <VehiclesSection />}
                        {hasLPR && (
                            <div className="mt-6">
                                <h3 className="font-weight-bold mb-5">
                                    <T>subscription.purchase.lpr.info.title</T>
                                </h3>
                                <p className={purchaseStyles.lprInfoContent}>
                                    <T>subscription.purchase.lpr.info.content</T>
                                </p>
                            </div>
                        )}
                        <AddressSection address={billingAddress} type="billing" />
                        {hasDelivery && (
                            <AddressSection
                                required={delivery?.prdctCd === DELIVERY_HOME || false}
                                address={deliveryAddress}
                                type="delivery"
                            />
                        )}
                    </>
                )}
                <PaymentMethodSection
                    selectedPaymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    paymentMethods={user?.creditCards}
                />
                {featureToggles.hasTermAndConditionsNewFlow ? (
                    <FooterSection
                        disabled={disabledSubmit}
                        loading={isLoading}
                        total={totalToPay}
                        termsAcceptedDate={termsAcceptedDate}
                        setTermsAcceptedDate={setTermsAcceptedDate}
                    />
                ) : (
                    <FooterSectionOld disabled={disabledSubmit} loading={isLoading} total={totalToPay} />
                )}
            </Formsy>
            {error && <ErrorMessage className="mt-5" messages={[error]} />}
        </CardSection>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            billingAddress: billingAddressSelector,
            userDeliveryAddress: deliveryAddressSelector,
            user: userSelector,
            reviewInvoices: reviewInvoicesSelector,
            pending: subscriptionPurchaseBuyPendingSelector,
            credentialType: credentialTypeSelector,
        }),
        { authVisible }
    )
)(ReviewPaySelection)
